import type { ComponentWidgetConfig, DynamicSectionCfg, ImageFlowZoneCfg, ImageOverlayPlace, SimpleLayoutCfg, SimpleWidgetCfg, WidgetType, ZoneCfg } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { normalizeShowWhen } from 'components/shared/base-product-tile/widget-rules.js'
import { ALWAYS_EXPORTED_PROPERTIES, DEFAULT_PROPERTIES } from './widget-metadata.js'
import { WIDGET_PROPERTY_SCHEMAS, isFieldVisible } from './widget-property-schema.js'
import type { DynamicAreaPadding, DynamicAreaSettings, ImageFlowSlot } from './context.js'
import type { AreaLayout, EditorRowLayout, Section, Widget } from './editor-state.js'

const zoneStyleFromSection = (section: Section | undefined): Omit<ZoneCfg, 'widgets'> => ({
  ...(section?.direction && { direction: section.direction }),
  ...(section?.justifyContent && { justifyContent: section.justifyContent }),
  ...(section?.alignItems && { alignItems: section.alignItems }),
  ...(section?.wrap && { wrap: section.wrap }),
  ...(section?.gap && { gap: section.gap }),
})

const stripIdPrefix = (id: string, idPrefix: string): string => id.startsWith(`${idPrefix}-`) ? id.slice(idPrefix.length + 1) : id

// productInfo/orderActions area-level settings — padding plus row/column gap (see
// DynamicAreaSettings). Reused as-is for image above/below's own padding (they share the same
// 4 padding fields); rowGap/columnGap are simply never populated for above/below since there's
// no row/column layout to space out there — see ImageFlowZoneCfg. The image area's own padding
// (as opposed to above/below's) is handled separately (image-config-mapper.ts's
// viewConfigFields) since it has no gap fields to share this with.
const AREA_SETTING_KEYS = ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'rowGap', 'columnGap', 'verticalAlign'] as const

const nonEmptyAreaSettings = (settings: DynamicAreaSettings | undefined): DynamicAreaSettings | undefined => {
  if (!settings) return undefined
  const filtered = AREA_SETTING_KEYS.reduce<DynamicAreaSettings>((acc, key) => {
    const value = settings[key]
    if (value) (acc as Record<string, string>)[key] = value
    return acc
  }, {})
  return Object.keys(filtered).length ? filtered : undefined
}

// A property whose every field is hidden for the widget's current settings (e.g. the request quote's
// link variant while it's a button) doesn't apply, so it isn't exported either.
const isHiddenProperty = (type: WidgetType, key: string, properties: ComponentWidgetConfig): boolean => {
  const fields = (WIDGET_PROPERTY_SCHEMAS[type] ?? []).filter(schema => schema.key === key)
  return fields.length > 0 && !fields.some(schema => isFieldVisible(schema, properties))
}

const nonDefaultProperties = (type: WidgetType, properties: ComponentWidgetConfig): ComponentWidgetConfig | undefined => {
  const defaults = DEFAULT_PROPERTIES[type] ?? {}
  const alwaysExported = ALWAYS_EXPORTED_PROPERTIES[type] ?? []
  const filtered = Object.entries(properties as Record<string, unknown>)
    .filter(([key, value]) => value !== undefined && (alwaysExported.includes(key) || value !== defaults[key]))
    .filter(([key]) => !isHiddenProperty(type, key, properties))
    .sort(([a], [b]) => a.localeCompare(b))
  return filtered.length ? Object.fromEntries(filtered) : undefined
}

const mapZoneWidgets = (widgets: Widget[]): SimpleWidgetCfg[] => widgets.map(w => {
  const properties = nonDefaultProperties(w.type, w.properties)
  return {
    type: w.type,
    ...(properties !== undefined && { properties }),
    ...(w.showWhen?.length && { showWhen: w.showWhen }),
  }
})

const widgetFromCfg = (
  widgetCfg: SimpleWidgetCfg,
  sectionId: string,
  index: number,
  createWidget: (type: Widget['type'], properties?: ComponentWidgetConfig) => Widget
): Widget => {
  const widget = createWidget(widgetCfg.type, widgetCfg.properties)
  widget.section = sectionId
  widget.sequence = index + 1
  const showWhen = normalizeShowWhen(widgetCfg)
  if (showWhen.length) widget.showWhen = showWhen
  return widget
}

const buildDynamicSectionCfg = (rows: EditorRowLayout[], sections: Section[], idPrefix: string, settings: DynamicAreaSettings | undefined): DynamicSectionCfg => {
  const hasAnyWidgets = rows.some(row => row.some(zoneId => (sections.find(s => s.id === zoneId)?.widgets.length ?? 0) > 0))
  const nonEmpty = nonEmptyAreaSettings(settings)
  if (!hasAnyWidgets) return nonEmpty ? { ...nonEmpty } : {}

  const zones: Record<string, ZoneCfg> = {}
  const layout = rows.map(row => row.map(zoneId => {
    const section = sections.find(s => s.id === zoneId)
    const widgets = (section?.widgets ?? []).slice().sort((a, b) => a.sequence - b.sequence)
    const bareZoneId = stripIdPrefix(zoneId, idPrefix)
    zones[bareZoneId] = {
      ...zoneStyleFromSection(section),
      widgets: mapZoneWidgets(widgets),
    }
    return bareZoneId
  }))
  return { ...nonEmpty, layout, zones }
}

// above/below are always exactly one zone (no layout/zones/rows — see ImageFlowZoneCfg), so
// unlike buildDynamicSectionCfg there's no row/zone traversal here, just this one section's own
// widgets + style + padding. Returns undefined (key omitted entirely from the exported image
// object) when there's nothing at all to say — no widgets, no configured style, no padding —
// same "omit rather than export an empty shell" convention the 8 corner places already use.
const buildImageFlowZoneCfg = (section: Section | undefined, padding: DynamicAreaSettings | undefined): ImageFlowZoneCfg | undefined => {
  const widgets = (section?.widgets ?? []).slice().sort((a, b) => a.sequence - b.sequence)
  const style = zoneStyleFromSection(section)
  const nonEmptyPadding = nonEmptyAreaSettings(padding) ?? {}
  if (!widgets.length && !Object.keys(style).length && !Object.keys(nonEmptyPadding).length) return undefined
  return { ...nonEmptyPadding, ...style, widgets: mapZoneWidgets(widgets) }
}

export const generateSimpleLayoutCfg = (sections: Section[], areaLayout: AreaLayout, areaPadding: DynamicAreaPadding = {}): SimpleLayoutCfg => {
  const cfg: SimpleLayoutCfg = { image: {}, productInfo: {}, orderActions: {} }

  const imageOverlayPlaces: ImageOverlayPlace[] = [
    'top-left', 'top-center', 'top-right',
    'middle-left', 'middle-right',
    'bottom-left', 'bottom-center', 'bottom-right',
  ]

  const hideUntilHoverPlaces: Partial<Record<ImageOverlayPlace, boolean>> = {}

  imageOverlayPlaces.forEach(place => {
    const section = sections.find(s => s.id === place)
    const widgets = (section?.widgets ?? []).slice().sort((a, b) => a.sequence - b.sequence)
    if (widgets.length) cfg.image[place] = mapZoneWidgets(widgets)
    if (section?.hideUntilHover) hideUntilHoverPlaces[place] = true
  })

  if (Object.keys(hideUntilHoverPlaces).length) cfg.image.hideUntilHoverPlaces = hideUntilHoverPlaces

  const aboveZone = buildImageFlowZoneCfg(sections.find(s => s.id === 'above'), areaPadding.above)
  if (aboveZone) cfg.image.above = aboveZone
  const belowZone = buildImageFlowZoneCfg(sections.find(s => s.id === 'below'), areaPadding.below)
  if (belowZone) cfg.image.below = belowZone

  cfg.productInfo = buildDynamicSectionCfg(areaLayout.productInfo, sections, 'product-info', areaPadding.productInfo)
  cfg.orderActions = buildDynamicSectionCfg(areaLayout.orderActions, sections, 'order-actions', areaPadding.orderActions)

  return cfg
}

interface DynamicAreaResult {
  sections: Section[]
  rows: EditorRowLayout[]
  settings: DynamicAreaSettings | undefined
}

const applyDynamicSectionCfg = (
  dyn: DynamicSectionCfg | undefined,
  idPrefix: string,
  dropAreaType: 'product-info' | 'product-footer',
  createWidget: (type: Widget['type'], properties?: ComponentWidgetConfig) => Widget
): DynamicAreaResult => {
  const normalized = dyn ?? {}
  const settings = nonEmptyAreaSettings(normalized)
  const rowsIn = normalized.layout?.length ? normalized.layout : [['zone-1']]
  const zonesIn = normalized.zones ?? {}
  const sections: Section[] = []

  const rows = rowsIn.map(row => row.map(zoneId => {
    const sectionId = `${idPrefix}-${zoneId}`
    const zoneCfg = zonesIn[zoneId]
    const widgets = (zoneCfg?.widgets ?? []).map((widgetCfg, index) => widgetFromCfg(widgetCfg, sectionId, index, createWidget))

    sections.push({
      id: sectionId,
      name: `Zone ${sections.length + 1}`,
      children_strategy: dropAreaType === 'product-footer' ? 'or' : 'and',
      widgets,
      dropAreaType,
      maxWidgets: 5,
      ...(zoneCfg?.direction && { direction: zoneCfg.direction }),
      ...(zoneCfg?.justifyContent && { justifyContent: zoneCfg.justifyContent }),
      ...(zoneCfg?.alignItems && { alignItems: zoneCfg.alignItems }),
      ...(zoneCfg?.wrap && { wrap: zoneCfg.wrap }),
      ...(zoneCfg?.gap && { gap: zoneCfg.gap }),
    })

    return sectionId
  }))

  return { sections, rows, settings }
}

const applyImageFlowZoneCfg = (
  zoneCfg: ImageFlowZoneCfg | undefined,
  baseSection: Section,
  createWidget: (type: Widget['type'], properties?: ComponentWidgetConfig) => Widget
): { section: Section; padding: DynamicAreaSettings | undefined } => {
  if (!zoneCfg) return { section: { ...baseSection, widgets: [] }, padding: undefined }

  const widgets = (zoneCfg.widgets ?? []).map((widgetCfg, index) => widgetFromCfg(widgetCfg, baseSection.id, index, createWidget))

  const section: Section = {
    ...baseSection,
    widgets,
    ...(zoneCfg.direction && { direction: zoneCfg.direction }),
    ...(zoneCfg.justifyContent && { justifyContent: zoneCfg.justifyContent }),
    ...(zoneCfg.alignItems && { alignItems: zoneCfg.alignItems }),
    ...(zoneCfg.wrap && { wrap: zoneCfg.wrap }),
    ...(zoneCfg.gap && { gap: zoneCfg.gap }),
  }

  return { section, padding: nonEmptyAreaSettings(zoneCfg) }
}

export const applySimpleLayoutCfg = (
  cfg: SimpleLayoutCfg,
  baseSections: Section[],
  createWidget: (type: Widget['type'], properties?: ComponentWidgetConfig) => Widget
): { sections: Section[]; areaLayout: AreaLayout; areaPadding: DynamicAreaPadding } => {
  const imageSections = baseSections
    .filter(s => s.dropAreaType === 'image-overlay')
    .map(s => ({ ...s, widgets: [] as Widget[] }))

  Object.entries(cfg.image || {}).forEach(([place, widgets]) => {
    if (place === 'above' || place === 'below') {
      const hasFlowSlot = baseSections.some(s => s.id === place && s.dropAreaType === 'image-flow')
      const flowWidgets = (widgets as ImageFlowZoneCfg | undefined)?.widgets ?? []
      if (!hasFlowSlot && flowWidgets.length) {
        console.warn(`csc-tile-configurator: dropped ${flowWidgets.length} widget(s) from image position "${place}" — no matching slot in this layout mode`)
      }
      return
    }
    const section = imageSections.find(s => s.id === place)
    if (section && Array.isArray(widgets)) {
      section.widgets = widgets.map((widgetCfg, index) => widgetFromCfg(widgetCfg, place, index, createWidget))
    } else if (Array.isArray(widgets) && widgets.length) {
      console.warn(`csc-tile-configurator: dropped ${widgets.length} widget(s) from image position "${place}" — no matching slot in this layout mode`)
    }
  })

  Object.entries(cfg.image?.hideUntilHoverPlaces ?? {}).forEach(([place, hideUntilHover]) => {
    const section = imageSections.find(s => s.id === place)
    if (section && hideUntilHover) section.hideUntilHover = true
  })

  const imageFlowAreaPadding: Partial<Record<ImageFlowSlot, DynamicAreaSettings>> = {}
  const imageFlowSections: Section[] = []
  ;(['above', 'below'] as const).forEach(slot => {
    const baseSection = baseSections.find(s => s.id === slot && s.dropAreaType === 'image-flow')
    if (!baseSection) return // grid mode has no above/below sections at all
    const { section, padding } = applyImageFlowZoneCfg(cfg.image?.[slot], baseSection, createWidget)
    imageFlowSections.push(section)
    if (padding !== undefined) imageFlowAreaPadding[slot] = padding
  })

  const productInfo = applyDynamicSectionCfg(cfg.productInfo, 'product-info', 'product-info', createWidget)
  const orderActions = applyDynamicSectionCfg(cfg.orderActions, 'order-actions', 'product-footer', createWidget)

  return {
    sections: [...imageSections, ...imageFlowSections, ...productInfo.sections, ...orderActions.sections],
    areaLayout: { productInfo: productInfo.rows, orderActions: orderActions.rows },
    areaPadding: {
      ...imageFlowAreaPadding,
      ...(productInfo.settings !== undefined && { productInfo: productInfo.settings }),
      ...(orderActions.settings !== undefined && { orderActions: orderActions.settings }),
    },
  }
}
